import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
const API_URL = `${BACKEND_URL.replace(/\/+$/, "")}/api`;

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 7000, // 7-second timeout to prevent infinite spinner
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach token automatically from localStorage if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: automatically handle 401 Unauthorized for expired or missing JWT tokens
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      const path = window.location.pathname;
      if (path === "/admin" || path === "/kitchen" || path === "/counter") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Fallback static tables for offline or server startup scenarios
export const DEFAULT_TABLES = [
  { id: "1", name: "Table 1", status: "available" },
  { id: "2", name: "Table 2", status: "available" },
  { id: "3", name: "Table 3", status: "available" },
  { id: "4", name: "Table 4", status: "available" },
  { id: "5", name: "Table 5", status: "available" },
  { id: "6", name: "Table 6", status: "available" },
  { id: "7", name: "Table 7", status: "available" },
  { id: "8", name: "Table 8", status: "available" },
  { id: "9", name: "Table 9", status: "available" },
];

export const orderApi = {
  baseUrl: API_URL,

  // Auth operations
  async login(email, password) {
    const res = await apiClient.post("/auth/login", { email, password });
    return res.data;
  },

  async getMe() {
    const res = await apiClient.get("/auth/me");
    return res.data;
  },

  // Table operations
  async getTables() {
    try {
      const res = await apiClient.get("/tables");
      if (Array.isArray(res.data) && res.data.length > 0) {
        return { data: res.data, isFallback: false };
      }
      return { data: DEFAULT_TABLES, isFallback: true };
    } catch (err) {
      console.warn("Could not reach backend tables API, using fallback tables:", err.message);
      return { data: DEFAULT_TABLES, isFallback: true, error: err };
    }
  },

  async addTable(name) {
    const res = await apiClient.post("/tables", { name });
    return res.data;
  },

  async deleteTable(tableId) {
    const res = await apiClient.delete(`/tables/${tableId}`);
    return res.data;
  },

  // Category & Product operations
  async getCategories() {
    const res = await apiClient.get("/categories");
    return res.data;
  },

  async getProducts() {
    const res = await apiClient.get("/products");
    return res.data;
  },

  async addCategory(name) {
    const res = await apiClient.post("/categories", { name });
    return res.data;
  },

  async addProduct(productData) {
    const res = await apiClient.post("/products", productData);
    return res.data;
  },

  async updateProduct(productId, productData) {
    const res = await apiClient.put(`/products/${productId}`, productData);
    return res.data;
  },

  async deleteProduct(productId) {
    const res = await apiClient.delete(`/products/${productId}`);
    return res.data;
  },

  // Order operations
  async createOrder(orderData) {
    const res = await apiClient.post("/orders", orderData);
    return res.data;
  },

  async getOrder(orderId) {
    const res = await apiClient.get(`/orders/${orderId}`);
    return res.data;
  },

  async getAllOrders() {
    const res = await apiClient.get("/orders");
    return res.data;
  },

  async updateOrderStatus(orderId, status) {
    const res = await apiClient.patch(`/orders/${orderId}/status`, { status });
    return res.data;
  },

  async updatePaymentStatus(orderId, paymentStatus) {
    const res = await apiClient.patch(`/orders/${orderId}/payment`, {
      payment_status: paymentStatus,
    });
    return res.data;
  },

  getInvoiceUrl(orderId) {
    return `${API_URL}/invoice/${orderId}`;
  },

  async downloadInvoiceBlob(orderId) {
    const res = await apiClient.get(`/invoice/${orderId}`, {
      responseType: "blob",
    });
    return res.data;
  },
};

export default orderApi;
